import { MeawwApi } from "../../utils/api.js";
import { setAppMessage } from "../../app-actions.js";
import { MessageTypes } from "../../app-constants/index.js";

export const MAX_ITEMS = 5;

// Normalize an article (from the GET override response or the article-search
// response) into the shape the UI works with. Handles camel/snake defensively.
function mapArticle(a) {
  const cover = a.coverPicture || a.cover_picture || "";
  const ext = a.imgExtension || a.img_extension || "";
  let thumbnail = null;
  if (cover) {
    const base = cover.indexOf("//") === 0 ? `https:${cover}` : cover;
    thumbnail = `${base}_450_250${ext}`;
  }
  return {
    articleId: a.articleId || a.article_id,
    articleName: a.articleName || a.article_name,
    title: a.title,
    url: a.url,
    thumbnail,
  };
}

// ---- Fetch current override (saved draft + published state)
export function fetchHomepageTrending() {
  return (dispatch, getState) => {
    dispatch({ type: "homepageTrending.fetch.request" });
    const { activeDomain } = getState().login.user;
    return MeawwApi().auth().then((api) =>
      api.get(`/homepage-trending/${activeDomain}`, (error, data) => {
        if (error) {
          dispatch(setAppMessage(error.message, { type: MessageTypes.ERROR }));
          dispatch({ type: "homepageTrending.fetch.failure" });
          return Promise.reject(error);
        }
        const result = (data && data.result) || {};
        dispatch({
          type: "homepageTrending.fetch.success",
          items: (result.items || []).map(mapArticle),
          isPublished: !!result.isPublished,
          publishedAt: result.publishedAt || null,
        });
        return Promise.resolve(data);
      }));
  };
}

// ---- Save the working list as a draft
export function saveHomepageTrending() {
  return (dispatch, getState) => {
    const { activeDomain } = getState().login.user;
    const { items } = getState().tool.homepageTrending;
    dispatch({ type: "homepageTrending.save.request" });
    const payload = { items: items.map((it) => ({ articleId: it.articleId, articleName: it.articleName })) };
    return MeawwApi().auth().then((api) =>
      api.put(`/homepage-trending/${activeDomain}`, payload, (error, data) => {
        if (error) {
          dispatch(setAppMessage(error.message, { type: MessageTypes.ERROR }));
          dispatch({ type: "homepageTrending.save.failure" });
          return Promise.reject(error);
        }
        dispatch({ type: "homepageTrending.save.success" });
        dispatch(setAppMessage("Draft saved", { type: MessageTypes.SUCCESS }));
        return Promise.resolve(data);
      }));
  };
}

// ---- Publish (server requires exactly 5 live articles)
export function publishHomepageTrending() {
  return (dispatch, getState) => {
    const { activeDomain } = getState().login.user;
    return MeawwApi().auth().then((api) =>
      api.post(`/homepage-trending/${activeDomain}/publish`, {}, (error, data) => {
        if (error) {
          dispatch(setAppMessage(error.message, { type: MessageTypes.ERROR }));
          return Promise.reject(error);
        }
        dispatch(setAppMessage("Published to homepage", { type: MessageTypes.SUCCESS }));
        dispatch(fetchHomepageTrending());
        return Promise.resolve(data);
      }));
  };
}

// ---- Revert to automatic (algorithmic) hero
export function revertHomepageTrending() {
  return (dispatch, getState) => {
    const { activeDomain } = getState().login.user;
    return MeawwApi().auth().then((api) =>
      api.post(`/homepage-trending/${activeDomain}/revert`, {}, (error, data) => {
        if (error) {
          dispatch(setAppMessage(error.message, { type: MessageTypes.ERROR }));
          return Promise.reject(error);
        }
        dispatch(setAppMessage("Reverted to automatic", { type: MessageTypes.SUCCESS }));
        dispatch(fetchHomepageTrending());
        return Promise.resolve(data);
      }));
  };
}

// ---- Article search: by title, or paste an article URL (slug -> title query)
export function searchArticles(rawQuery) {
  return (dispatch, getState) => {
    const { activeDomain } = getState().login.user;
    let searchQuery = (rawQuery || "").trim();
    if (searchQuery.indexOf("/") > -1 || searchQuery.indexOf("http") === 0) {
      const parts = searchQuery.split("?")[0].split("#")[0].split("/").filter(Boolean);
      const slug = parts.length ? parts[parts.length - 1] : searchQuery;
      searchQuery = slug.replace(/-/g, " ");
    }
    if (!searchQuery) return Promise.resolve();
    dispatch({ type: "homepageTrending.search.request", query: searchQuery });
    return MeawwApi().auth().then((api) =>
      api.post(`/article/${activeDomain}/search`, {
        searchQuery, category: [], subcategory: [], author: "", page: 1, perPage: 50,
      }, (error, data) => {
        if (error) {
          dispatch(setAppMessage(error.message, { type: MessageTypes.ERROR }));
          dispatch({ type: "homepageTrending.search.failure" });
          return Promise.reject(error);
        }
        dispatch({ type: "homepageTrending.search.success", results: ((data && data.result) || []).map(mapArticle) });
        return Promise.resolve(data);
      }));
  };
}

// ---- Sync UI / working-list actions
export function openPicker(slotIndex) {
  return { type: "homepageTrending.picker.open", slotIndex };
}
export function closePicker() {
  return { type: "homepageTrending.picker.close" };
}
export function selectArticle(article) {
  return { type: "homepageTrending.select", article };
}
export function setItems(items) {
  return { type: "homepageTrending.items.set", items };
}
